/**
 * The FLOU pricing engine — PURE. Zero imports from `@repo/db` or any I/O: it
 * takes plain numbers and returns plain numbers, so every branch is
 * unit-testable and the same math runs in the creator preview, the checkout
 * recompute (P6) and the panel.
 *
 * **All money is integral grosze, net**, matching the schema.
 *
 * ── THERE IS NO AUTOMATIC DISCOUNTING ANY MORE ───────────────────────────
 * This engine used to own three ladders: VOLUME (more pieces, cheaper each),
 * RENTAL_DAYS (longer hire, cheaper per day) and MULTI_SERVICE (a bonus for
 * combining services). `quoteOrder` priced every basket twice and charged the
 * cheaper of the two, because spec §14 said the first and third must not stack.
 *
 * The owner removed all of it: **the only discount is a code an admin issues.**
 * A price on the site is now the price — what the catalogue says, times the
 * quantity — and the only thing that can move it is `discount_code`, applied
 * once, at checkout, in `orders.service.ts`.
 *
 * So the rungs are gone from this file rather than left switched off. A ladder
 * nobody applies is a price nobody can explain: the next reader would find
 * `bundleQuote` exported beside the live arithmetic and have no way to tell
 * which of the two the customer was charged by.
 *
 * What survived is what was never a discount: rounding, VAT, and the day
 * arithmetic a hire's window is measured in.
 *
 * `parsePrice("Wycena indywidualna") → 0` is still structurally impossible: a
 * quote-only line has no numeric price to parse (the schema stores NULL +
 * `isQuoteOnly`), so it can never be read as a free line.
 */
import type { RoundingMode } from './constants.ts'

export type { PricingPolicy, RoundingMode } from './constants.ts'
/**
 * Re-exported so `@repo/core/pricing` is a COMPLETE leaf.
 *
 * That export exists for one reason: the browser needs this arithmetic, and
 * `@repo/core/offering` cannot give it — that barrel also exports the offering
 * SERVICE, which reaches into `@repo/db`, so importing it from a client
 * component drags the database layer into the bundle. This module imports
 * nothing but `./constants.ts`, which imports nothing at all.
 *
 * A consumer that had to reach past this file for a type would be back to
 * importing the barrel, so the types come along.
 */
export { DEFAULT_PRICING_POLICY } from './constants.ts'

/**
 * Round a raw grosze amount per policy. `whole-zloty` snaps to the nearest whole
 * złoty (matching the live site's `Math.round` over złoty-valued inputs);
 * `grosz-exact` keeps grosze. Both use round-half-up, as `Math.round` does.
 */
export function roundMinor(amountMinor: number, mode: RoundingMode): number {
  return mode === 'whole-zloty' ? Math.round(amountMinor / 100) * 100 : Math.round(amountMinor)
}

/** Gross (VAT-inclusive) grosze from a net amount, per policy rounding. */
export function grossMinor(netMinor: number, taxRateBps: number, mode: RoundingMode): number {
  const factor = 1 + taxRateBps / 10_000
  return mode === 'whole-zloty'
    ? Math.round((netMinor / 100) * factor) * 100
    : Math.round(netMinor * factor)
}

/** The VAT itself, as gross − net — never a separate recomputation (see `money.ts`). */
export function vatOf(netMinor: number, taxRateBps: number, mode: RoundingMode): number {
  return grossMinor(netMinor, taxRateBps, mode) - netMinor
}

/**
 * Inclusive day count: picking the same day for both ends is one day. Lifted
 * verbatim from `rental.ts` so the creator, the booking modal and the checkout
 * recompute can never disagree about what a "day" is — the price follows
 * directly from it. Returns 0 for missing / malformed / reversed ranges.
 */
export function countDays(fromIso: string, toIso: string): number {
  if (!fromIso || !toIso) return 0
  const a = Date.parse(fromIso)
  const b = Date.parse(toIso)
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return 0
  return Math.round((b - a) / 86_400_000) + 1
}

/**
 * Polish plural for "doba" (a 24h rental day): 1 doba, 2–4 doby, 5+ dób — the
 * teens take the 5+ form. From `rental.ts`; kept in the engine because it
 * describes the same day count the price is computed from.
 */
export function dayWord(n: number): string {
  if (n === 1) return 'doba'
  const last = n % 10
  const lastTwo = n % 100
  if (last >= 2 && last <= 4 && !(lastTwo >= 12 && lastTwo <= 14)) return 'doby'
  return 'dób'
}

/**
 * One basket line as the totals function reads it.
 *
 * `lineTotalMinor` is NULL for a quote-only line, which is then excluded from
 * the sum: a line with no price cannot contribute to a total, and reading it as
 * zero would let an enquiry silently pass as a free item.
 *
 * The shape lost three fields when the discounts went — `isRental`,
 * `serviceKey` and `countsTowardMultiService` existed only to count the
 * DISTINCT services a basket held, which is what the MULTI_SERVICE bonus was
 * keyed to. Nothing is keyed to it any more.
 */
export interface CartLine {
  readonly lineTotalMinor: number | null
  readonly isQuoteOnly: boolean
}

export interface CartTotals {
  /** Sum of the priced lines, net grosze. */
  readonly subtotalMinor: number
  /**
   * The same number as {@link subtotalMinor}, and deliberately still here.
   *
   * Nothing sits between the two now that the basket bonus is gone. The pair is
   * kept because a discount CODE still lands after this — in
   * `orders.service.ts`, against the total — and callers that print a
   * "subtotal" and a "total" separately stay correct if anything ever comes
   * back in between.
   */
  readonly totalMinor: number
}

/** Add up what the basket costs. Quote-only lines carry no money and are skipped. */
export function cartTotals(lines: readonly CartLine[]): CartTotals {
  const subtotalMinor = lines.reduce(
    (acc, line) =>
      acc + (line.isQuoteOnly || line.lineTotalMinor === null ? 0 : line.lineTotalMinor),
    0,
  )
  return { subtotalMinor, totalMinor: subtotalMinor }
}
